import axios from "axios";
import { installRequestCounter } from "../utils/requestCounter";

const api = axios.create();

/** Upper bound on the token refresh a 401 triggers inside the interceptor. */
const REFRESH_TOKEN_TIMEOUT = 15000;

api.interceptors.request.use(
  (request) => {
    const token = localStorage.getItem("Token");
    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    }
    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * The single refresh currently in flight, or null.
 *
 * Without this, every request that happens to be open when the token expires
 * starts its own refresh. App.tsx alone fires eight on mount, so an expiry
 * during startup produced eight concurrent POSTs to /Account/RefreshToken. If
 * the backend rotates the refresh token, only the first of those can succeed
 * and the rest fail, write Token="end", and the session-expiry interval in
 * App.tsx reads exactly that value to force a logout: the cashier is thrown out
 * mid-shift by a token refresh that actually worked.
 *
 * Whether the backend rotates on use is still an open question in the plan.
 * Single-flight makes it moot either way, because there is never more than one
 * refresh to invalidate.
 */
/**
 * Written to `Token` when a refresh fails for good. The session-expiry interval
 * in App.tsx watches this exact value to force the logout, and the guard in
 * refreshAccessToken reads it so a session already sentenced stops asking.
 */
const SESSION_ENDED = "end";

/**
 * The refresh credential that already failed. A new login writes a new one, so
 * this clears itself without anyone having to remember to reset it.
 */
let deadRefreshToken: string | null = null;

/**
 * Called by Login after a successful sign-in. A new session must be able to
 * refresh even if the previous one died on this same page load, and relying on
 * the server handing out a different credential would be a guess.
 */
export const resetSessionGuard = () => {
  deadRefreshToken = null;
};

let refreshPromise: Promise<string> | null = null;

const refreshAccessToken = (): Promise<string> => {
  const refreshToken = localStorage.getItem("RefreshToken");

  // Three ways a refresh is known to be pointless before it is sent. Found in
  // QA: without them a dying session produced three refreshes, one of them
  // after the logout had already happened.
  //
  //  - No credential. logoutSession calls localStorage.clear(), so everything
  //    still in flight when the cashier is thrown out would otherwise send
  //    TokenHash: null, which the API can only reject.
  //  - The sentinel is already written, so the logout is a second away.
  //  - This exact credential failed once. The sentinel alone does not cover
  //    this, because the logout wipes it from storage while requests are still
  //    landing.
  if (
    !refreshToken ||
    localStorage.getItem("Token") === SESSION_ENDED ||
    refreshToken === deadRefreshToken
  ) {
    return Promise.reject(new Error("La sesión expiró."));
  }

  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const baseURL = process.env.REACT_APP_API_HOST;
    try {
      // Bounded on purpose: a per-request `timeout` only covers the original
      // call. Parking here instead leaves the caller's promise unsettled
      // forever, and the single-flight promise awaited by several requests
      // hangs every one of them with no error and no retry.
      const response = await axios.post(
        `${baseURL}/Account/RefreshToken`,
        {
          TokenHash: localStorage.getItem("RefreshToken"),
          UserID: localStorage.getItem("UserID"),
        },
        { timeout: REFRESH_TOKEN_TIMEOUT }
      );

      if (response.data.error) throw response.data.error;

      const newToken = response.data.tokenHash;
      localStorage.setItem("Token", newToken);
      return newToken as string;
    } catch (refreshError) {
      // "end" is the sentinel the session interval in App.tsx watches to force
      // a logout. It is set once here, by the one refresh that ran.
      deadRefreshToken = refreshToken;
      localStorage.setItem("Token", SESSION_ENDED);
      throw refreshError;
    }
  })().finally(() => {
    // Never memoize the outcome: a later 401 must be free to try again.
    refreshPromise = null;
  });

  return refreshPromise;
};

api.interceptors.response.use(
  async (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // error.response is undefined on a network failure, a timeout or a CORS
    // rejection. Reading .status unguarded turned every one of those into a
    // TypeError thrown from inside the interceptor, so callers never saw the
    // axios error their catch blocks were written for.
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        // Retried on `api`, not on the default axios instance: the retry has to
        // go through the same interceptor chain as the original request.
        return api(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Phase 0 baseline. The literal comparison is what lets the minifier drop the
// whole counter from builds that do not set the flag, instead of shipping dead
// code to production; installRequestCounter guards itself again anyway.
// Both instances are passed: `api` carries most traffic, but a handful of
// services still call raw `axios`, and interceptors are not inherited across
// instances — that is why NetworkMonitor only ever saw a fraction of the calls.
if (process.env.REACT_APP_REQUEST_COUNTER === "true") {
  installRequestCounter(api, axios);
}

export default api;
